import { format, formatDistanceToNowStrict } from 'date-fns';
import { memo, useCallback } from 'react';
import { useVitals } from '@/data/sources/vitals';
import { ContextualPopup, Divider, Flex, Typography } from '@/ui';
import { ECGChart } from '../ECGChart/ECGChart';
import { HeartRateChart } from '../HeartRateChart/HeartRateChart';
import { HeartRateIndicator } from '../HeartRateIndicator/HeartRateIndicator';
import { VitalsPopupHandle } from './VitalsPopupHandle';
import { useDeviceMotion } from '@/hooks/useDeviceMotion';

export const VitalsPopup = memo(() => {
  const { data, isLoading, error } = useVitals();
  const { requestPermission } = useDeviceMotion();

  const getDate = useCallback((i: number) => {
    return new Date(Date.now() - (i + Math.random() * 10) * 1000 * 60 * 60);
  }, []);

  const onOpenHandler = useCallback(
    (open: boolean) => {
      if (!open) return;
      requestPermission();
    },
    [requestPermission]
  );

  if (isLoading || error || !data) {
    return null;
  }

  const {
    heartRate,
    heartRateHistory,
    restingHeartRate,
    oxygenSaturation,
    steps,
    activeEnergy,
    lastSync,
    source,
  } = data;

  const vitalStats = [
    {
      label: 'Resting',
      value:
        restingHeartRate != null ? `${Math.round(restingHeartRate)} BPM` : '–',
    },
    {
      label: 'Blood O₂',
      value:
        oxygenSaturation != null
          ? `${Math.round(oxygenSaturation * 100)}%`
          : '–',
    },
    {
      label: 'Steps',
      value: steps != null ? Math.round(steps).toLocaleString() : '–',
    },
    {
      label: 'Active',
      value: activeEnergy != null ? `${Math.round(activeEnergy)} kcal` : '–',
    },
  ];

  return (
    <ContextualPopup
      behaviour="click"
      handle={<VitalsPopupHandle />}
      onOpenChange={onOpenHandler}
    >
      <ContextualPopup.Section>
        <Flex justifyContent="space-between">
          <Flex flexDirection="column">
            <Typography.Caption size="L">Heart Rate</Typography.Caption>

            <Flex gap={4} alignItems="center">
              <HeartRateIndicator heartRate={heartRate} />

              <Typography.Caption opacity={0.5}>
                {Math.floor(heartRate ?? 0)} BPM
              </Typography.Caption>
            </Flex>
          </Flex>

          <HeartRateChart
            heartRateHistory={
              heartRateHistory ??
              Array.from({ length: 10 }, (_, i) => ({
                date: getDate(i),
                value: 0,
              }))
            }
            heartRateExtrapolation={heartRate ?? 0}
          />
        </Flex>
      </ContextualPopup.Section>
      <Divider.Horizontal />

      <ContextualPopup.Section>
        <Flex justifyContent="space-between">
          <Flex flexDirection="column">
            <Typography.Caption size="L">Electrocardiogram</Typography.Caption>
            <Typography.Caption opacity={0.5}>
              {heartRate
                ? 'Extrapolated from real EKG sample'
                : 'No signs of life'}
            </Typography.Caption>
          </Flex>

          <ECGChart heartRate={heartRate} />
        </Flex>
      </ContextualPopup.Section>

      <Divider.Horizontal />

      <ContextualPopup.Section>
        <Flex justifyContent="space-between" gap={16}>
          {vitalStats.map((stat) => (
            <Flex key={stat.label} flexDirection="column" alignItems="start">
              <Typography.Caption>{stat.value}</Typography.Caption>
              <Typography.Caption opacity={0.5}>
                {stat.label}
              </Typography.Caption>
            </Flex>
          ))}
        </Flex>
      </ContextualPopup.Section>

      <Divider.Horizontal />

      <ContextualPopup.Section>
        <Flex justifyContent="space-between" alignItems="center">
          <Flex flexDirection="column" alignItems="start">
            <Typography.Caption size="L">
              Last Synchronization
            </Typography.Caption>

            {source && source.toLowerCase() !== 'unknown' && (
              <Typography.Caption opacity={0.5} textAlign="left">
                from{' '}
                {source.startsWith('iOS')
                  ? 'iPhone'
                  : source.startsWith('watchOS')
                    ? 'Apple Watch'
                    : 'Unknown device'}
              </Typography.Caption>
            )}
          </Flex>

          {lastSync ? (
            <Flex flexDirection="column" alignItems="end">
              <Typography.Caption size="L">
                {formatDistanceToNowStrict(lastSync, {
                  addSuffix: true,
                })}
              </Typography.Caption>

              <Typography.Caption opacity={0.5} textAlign="left">
                {format(lastSync, 'dd. MMM. yyyy, p')}
              </Typography.Caption>
            </Flex>
          ) : (
            <Typography.Caption opacity={0.5} textAlign="left">
              None in the last 24 hours
            </Typography.Caption>
          )}
        </Flex>
      </ContextualPopup.Section>

      <Divider.Horizontal />

      <ContextualPopup.Section>
        <Typography.Caption opacity={0.5} textAlign="left">
          This is my (almost) real-time heart rate synchronised directly with my
          Apple Watch as frequently as the system allows. This data is
          extrapolated from recent historical data to provide a continuous
          heartbeat prediction.
          <br />
          <br />
          Fun fact; the beating heart animation is also synchronised with my
          real heart rate.
        </Typography.Caption>
      </ContextualPopup.Section>
    </ContextualPopup>
  );
});
